import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCreateInvitation } from '@/features/studio/hooks/useStudio';
import { Copy, Check, UserPlus, Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function InviteDialog({ open, onOpenChange }: Props) {
  const [role, setRole] = useState<'resident' | 'guest'>('resident');
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const createInvitation = useCreateInvitation();

  const handleGenerate = async () => {
    try {
      const url = await createInvitation.mutateAsync({ role });
      setLink(url);
    } catch {
      toast.error("Impossible de créer le lien d'invitation.");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Lien copié !');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Invitation Consentink", text: "Rejoins mon studio sur Consentink", url: link });
      } catch (e: any) {
        if (e?.name !== 'AbortError') handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  const handleClose = () => { setLink(''); setCopied(false); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={18} className="text-primary" />
            Inviter un artiste
          </DialogTitle>
          <DialogDescription>
            Générez un lien d'invitation valable 48h pour un résident ou un guest.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Rôle</label>
            <div className="flex gap-2">
              {(['resident', 'guest'] as const).map(r => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-colors ${
                    role === r
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/40'
                  }`}
                >
                  {r === 'resident' ? '🏠 Résident' : '👤 Guest'}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock size={11} /> Le lien expire après 48h
            </p>
          </div>

          {!link ? (
            <button
              onClick={handleGenerate}
              disabled={createInvitation.isPending}
              className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm disabled:opacity-50"
            >
              {createInvitation.isPending ? 'Génération…' : 'Générer le lien'}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="p-3 bg-muted rounded-xl text-xs font-mono break-all text-muted-foreground select-all">
                {link}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm"
                >
                  {copied ? <Check size={15} /> : <Copy size={15} />}
                  {copied ? 'Copié !' : 'Copier'}
                </button>
                <button
                  onClick={handleShare}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-secondary text-secondary-foreground rounded-xl font-semibold text-sm hover:bg-secondary/80 transition-colors"
                >
                  <Share2 size={15} />
                  Partager
                </button>
              </div>
              <button
                onClick={handleGenerate}
                className="w-full py-2 text-xs text-muted-foreground hover:text-foreground"
              >
                Générer un nouveau lien
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
